import React, { useEffect, useRef, useState } from 'react'
import { Upload, Loader2 } from 'lucide-react'
import { tenantDataAPI } from '../../services/api'
import { Tag, StatusLabel, SearchInput, Spinner, ErrorBanner } from '../ui'
import { formatDate } from '../../lib/support'

const CLIENT_STATUS_LABEL = {
  prospect: 'Prospecto',
  active: 'Ativo',
  inactive: 'Inativo',
  completed: 'Concluído',
  deleted: 'Excluído',
}

// Owner only. Search is server-side (phone matches on digits, so spaces, dashes and
// parentheses don't matter); the palette deep-links here with ?q= and ?deleted=1.
export const ClientsTab = ({ tenantId, initialQuery = '', initialIncludeDeleted = false }) => {
  const [query, setQuery] = useState(initialQuery)
  const [includeDeleted, setIncludeDeleted] = useState(initialIncludeDeleted)
  const [clients, setClients] = useState([])
  const [pagination, setPagination] = useState(null)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [csvImporting, setCsvImporting] = useState(false)
  const [csvImportResult, setCsvImportResult] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)
  const csvInputRef = useRef(null)

  useEffect(() => setQuery(initialQuery), [initialQuery])
  useEffect(() => setIncludeDeleted(initialIncludeDeleted), [initialIncludeDeleted])
  useEffect(() => setPage(1), [query, includeDeleted])

  useEffect(() => {
    let cancelled = false
    const id = setTimeout(async () => {
      setLoading(true)
      setError('')
      try {
        const params = { page, per_page: 50 }
        if (includeDeleted) params.include_deleted = 'true'
        if (query.trim()) params.search = query.trim()
        const res = await tenantDataAPI.getTenantClients(tenantId, params)
        if (cancelled) return
        setClients(res.data.data || [])
        setPagination(res.data.pagination || null)
      } catch (err) {
        if (cancelled) return
        setClients([])
        setPagination(null)
        setError(`Não foi possível carregar os clientes: ${err.response?.data?.error?.message || err.message}`)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(id)
    }
  }, [tenantId, query, includeDeleted, page, reloadKey])

  const handleImportClientsCSV = async (file) => {
    setCsvImporting(true)
    setCsvImportResult(null)
    setError('')
    try {
      const res = await tenantDataAPI.importTenantClientsCSV(tenantId, file)
      setCsvImportResult(res.data?.data || null)
      setPage(1)
      setReloadKey((k) => k + 1)
    } catch (err) {
      setError(`Falha na importação do CSV: ${err.response?.data?.error?.message || err.message}`)
    } finally {
      setCsvImporting(false)
      if (csvInputRef.current) csvInputRef.current.value = ''
    }
  }

  return (
    <section className="card flex flex-col gap-3 overflow-x-auto p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="flex-1 text-sm">Clientes</h4>
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder="E-mail, nome ou telefone"
          aria-label="Pesquisar clientes do estúdio"
          className="min-w-[240px]"
          inputClassName="bg-admin-50"
        />
        <label className="flex cursor-pointer items-center gap-1.5 text-[13px]">
          <input
            type="checkbox"
            className="rounded border-admin-300 text-primary-600 focus:ring-primary-500"
            checked={includeDeleted}
            onChange={(e) => setIncludeDeleted(e.target.checked)}
          />
          Incluir excluídos
        </label>
        <input
          ref={csvInputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) handleImportClientsCSV(file)
          }}
        />
        <button
          type="button"
          className="btn btn-secondary"
          disabled={csvImporting}
          onClick={() => csvInputRef.current?.click()}
          title="Importa clientes de um arquivo CSV (colunas: Nome, Whatsapp, E-mail). Uma linha é aceita se tiver um e-mail válido ou um telefone."
        >
          {csvImporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          {csvImporting ? 'Importando…' : 'Carregar base de clientes'}
        </button>
      </div>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      {csvImportResult && (
        <div className="rounded-md border border-admin-200 bg-admin-50 p-3">
          <div className="flex items-start justify-between gap-3 text-[13px]">
            <div>
              <p className="mb-0.5 font-semibold">Importação do CSV concluída</p>
              <p>
                <span className="font-medium text-green-800">{csvImportResult.imported}</span> importados ·{' '}
                <span className="font-medium">{csvImportResult.skipped}</span> ignorados ·{' '}
                <span className="text-admin-600">{csvImportResult.total_rows} linhas</span>
                {csvImportResult.limit_reached && (
                  <Tag tone="warn" className="ml-2">
                    limite do plano atingido
                  </Tag>
                )}
              </p>
              {csvImportResult.imported_without_email > 0 && (
                <p className="mt-1 text-xs text-admin-600">
                  {csvImportResult.imported_without_email} deles não tinham e-mail — foram salvos apenas com o telefone. Use
                  o telefone para iniciar o contato.
                </p>
              )}
            </div>
            <button type="button" className="btn btn-ghost" onClick={() => setCsvImportResult(null)}>
              Dispensar
            </button>
          </div>
          {csvImportResult.skipped_rows?.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-xs text-admin-600 hover:text-admin-900">
                Ver {csvImportResult.skipped_rows.length} {csvImportResult.skipped_rows.length === 1 ? 'linha ignorada' : 'linhas ignoradas'}
              </summary>
              <div className="mono mt-2 max-h-48 space-y-1 overflow-y-auto text-xs text-admin-700">
                {csvImportResult.skipped_rows.map((r, i) => (
                  <div key={i} className="flex gap-2">
                    <span className="w-12 text-admin-400">#{r.row}</span>
                    <span className="w-40 text-admin-500">{r.reason}</span>
                    <span className="flex-1 break-all">
                      {r.email || r.phone || '—'} {r.name ? `(${r.name})` : ''}
                    </span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {loading && clients.length === 0 ? (
        <Spinner />
      ) : clients.length === 0 ? (
        <p className="text-[13px] text-admin-600">
          {query.trim()
            ? `Nenhum cliente corresponde a “${query.trim()}”. Se um lead diz que enviou um formulário, marque Incluir excluídos e depois confira o registro de Formulários públicos.`
            : 'Nenhum cliente neste estúdio.'}
        </p>
      ) : (
        <table className={`data-table min-w-[860px] ${loading ? 'opacity-60' : ''}`}>
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Status</th>
              <th>Produtos</th>
              <th>Relatórios</th>
              <th>Consegue entrar</th>
              <th>Adicionado em</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => {
              const products = c.products_assigned || []
              const active = products.filter((p) => p.is_active).map((p) => p.product_name || p.product_id)
              const productsLabel = active.length
                ? active.join(', ')
                : products.length
                  ? `${products.length} ${products.length === 1 ? 'atribuição anterior' : 'atribuições anteriores'}`
                  : '—'
              const flags = [c.deleted_at && 'excluído', c.spam_suspect && 'suspeita de spam', !c.email && 'só telefone'].filter(Boolean)
              return (
                <tr key={c.id}>
                  <td>
                    <div className="flex flex-col leading-snug">
                      <span className="font-semibold">{c.name || '—'}</span>
                      <span className="mono text-xs text-admin-600">{[c.email, c.phone].filter(Boolean).join(' · ')}</span>
                    </div>
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      <Tag tone="muted">{CLIENT_STATUS_LABEL[c.client_status] || c.client_status}</Tag>
                      {flags.map((f) => (
                        <Tag key={f} tone="crit">
                          {f}
                        </Tag>
                      ))}
                    </div>
                  </td>
                  <td className="max-w-[200px]">{productsLabel}</td>
                  <td className="tabular-nums">{c.reports_uploaded_count ?? 0}</td>
                  <td>
                    <div className="flex flex-col gap-0.5">
                      <StatusLabel tone={c.can_login ? 'ok' : 'muted'} className="font-semibold">
                        {c.can_login ? 'Sim' : 'Não'}
                      </StatusLabel>
                      {!c.can_login && (
                        <span className="text-xs text-admin-600">{c.cannot_login_reason || 'Não consegue entrar'}</span>
                      )}
                    </div>
                  </td>
                  <td className="whitespace-nowrap">{formatDate(c.created_at)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      {pagination && pagination.pages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-admin-100 pt-3 text-[13px] text-admin-600">
          <span>
            Página {pagination.page} de {pagination.pages} · {pagination.total} no total
          </span>
          <div className="flex gap-2">
            <button type="button" className="btn btn-secondary" disabled={loading || page <= 1} onClick={() => setPage((p) => p - 1)}>
              Anterior
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={loading || page >= pagination.pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Próxima
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
